import { getApiEndpoint } from '../config/apiEndpoints';
import { Book, BookNote, BookHistoryEvent } from '../objects/Book';
import { fetchWithAuth } from '../utils/tokenRefresh';

export const listBooks = async (): Promise<Book[]> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/books'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'list_books' }),
  });

  if (!response.ok) {
    throw new Error('Failed to load books');
  }

  const result = await response.json();
  return result.books ?? [];
};

export const createBook = async (data: Partial<Book>): Promise<Book> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/books'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'create_book', ...data }),
  });

  if (!response.ok) {
    throw new Error('Failed to create book');
  }

  const result = await response.json();
  return result.book;
};

export const updateBook = async (
  data: Partial<Book> & { uuid: string; old_status?: string }
): Promise<Book> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/books'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'update_book', ...data }),
  });

  if (!response.ok) {
    throw new Error('Failed to update book');
  }

  const result = await response.json();
  return result.book;
};

export const updateQueue = async (orderedIds: string[]): Promise<void> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/books'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'update_queue', ordered_ids: orderedIds }),
  });

  if (!response.ok) {
    throw new Error('Failed to update queue');
  }
};

export const listNotes = async (bookUuid: string): Promise<BookNote[]> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/notes'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'list_notes', book_uuid: bookUuid }),
  });

  if (!response.ok) {
    throw new Error('Failed to load notes');
  }

  const result = await response.json();
  return result.notes ?? [];
};

export const createNote = async (bookUuid: string, content: string): Promise<BookNote> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/notes'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'create_note', book_uuid: bookUuid, content }),
  });

  if (!response.ok) {
    throw new Error('Failed to create note');
  }

  const result = await response.json();
  return result.note;
};

export const updateNote = async (uuid: string, content: string): Promise<BookNote> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/notes'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'update_note', uuid, content }),
  });

  if (!response.ok) {
    throw new Error('Failed to update note');
  }

  const result = await response.json();
  return result.note;
};

export const getBookHistory = async (bookUuid: string): Promise<BookHistoryEvent[]> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/history'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'get_book_history', book_uuid: bookUuid }),
  });

  if (!response.ok) {
    throw new Error('Failed to load book history');
  }

  const result = await response.json();
  return result.history ?? [];
};

export const getAllHistory = async (limit: number = 50): Promise<BookHistoryEvent[]> => {
  const response = await fetchWithAuth(getApiEndpoint('READING', '/history'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_type: 'get_all_history', limit }),
  });

  if (!response.ok) {
    throw new Error('Failed to load history');
  }

  const result = await response.json();
  return result.history ?? [];
};
